import React, { useState, useRef, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CountryCodeDropdown from './CountryCodeDropdown';
import CountryDropdown from './CountryDropdown';
import ItemTypeDropdown from './ItemTypeDropdown';
import { useTranslation } from 'react-i18next';

export default function FoundItemRegistration() {
  const { t } = useTranslation();
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  
  // Item details
  const [itemType, setItemType] = useState('');
  const [itemName, setItemName] = useState('');
  const [dateFound, setDateFound] = useState(getTodayDate()); // Default to today
  const [roomNumber, setRoomNumber] = useState('');
  const [hotelComplex, setHotelComplex] = useState('');
  const [otherLocation, setOtherLocation] = useState('');
  const [storageLocation, setStorageLocation] = useState('');
  const [description, setDescription] = useState('');
  
  // Finder details
  const [finderName, setFinderName] = useState('');
  
  // Owner details
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [dateLost, setDateLost] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [country, setCountry] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [foundByNonHotelStaff, setFoundByNonHotelStaff] = useState('');
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedItemId, setSubmittedItemId] = useState(null);
  const { currentUser, userClaims } = useAuth();
  const navigate = useNavigate();
  const { hotelId } = useParams();
  const [isStaffOrAdmin, setIsStaffOrAdmin] = useState(false);
  
  // Counter for cycling through test data variations
  const testDataCounter = useRef(0);

  // Test data variations
  const testDataVariations = [
    // Variation 1: Japanese smartphone
    {
      itemType: 'Phone',
      itemName: 'iPhone 13 Pro',
      dateFound: '2025-11-15',
      locationLost: 'ロビー',
      cashValue: '75000',
      comments: '白色のiPhone、画面に軽い傷あり',
      finderName: '山田太郎',
      firstName: '鈴木',
      surname: '一郎',
      dateLost: '2025-11-14',
      countryCode: '+81',
      phoneNumber: '90-1234-5678',
      email: 'suzuki@example.com',
      address: '東京都渋谷区神宮前1-1-1',
      country: 'Japan',
      organisation: 'Tokyo International School',
      foundByNonHotelStaff: 'ホテルスタッフ以外の発見者',
      imageUrl: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8aXBob25lfGVufDB8fDB8fHww'
    },
    
    // Variation 2: Wallet with cash
    {
      itemType: 'Wallet',
      itemName: 'Brown Leather Wallet',
      dateFound: '2025-12-05',
      locationLost: 'Restaurant',
      cashValue: '15000',
      comments: 'Contains ID cards and credit cards. Brown leather with initials "JD"',
      finderName: 'Emma Wilson',
      firstName: 'John',
      surname: 'Doe',
      dateLost: '2025-12-04',
      countryCode: '+1',
      phoneNumber: '555-123-4567',
      email: 'john.doe@example.com',
      address: '123 Main St, New York, NY 10001',
      country: 'United States',
      organisation: '',
      foundByNonHotelStaff: '',
      imageUrl: 'https://images.unsplash.com/photo-1556741533-6e6a62bd8b49?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8d2FsbGV0fGVufDB8fDB8fHww'
    },
    
    // Variation 3: Jewelry
    {
      itemType: 'Jewelry',
      itemName: 'Gold Necklace',
      dateFound: '2025-10-22',
      locationLost: 'Swimming Pool',
      cashValue: '50000',
      comments: 'Gold necklace with heart pendant. Possibly has sentimental value.',
      finderName: 'Carlos Rodriguez',
      firstName: 'Maria',
      surname: 'Garcia',
      dateLost: '2025-10-21',
      countryCode: '+34',
      phoneNumber: '612-345-678',
      email: 'maria.garcia@example.com',
      address: 'Calle Mayor 12, Madrid',
      country: 'Spain',
      organisation: 'Madrid University Dance Club',
      foundByNonHotelStaff: 'Pool Maintenance Staff',
      imageUrl: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8aXBob25lfGVufDB8fDB8fHww'
    },
    
    // Variation 4: Electronics
    {
      itemType: 'Laptop',
      itemName: 'Laptop (MacBook Pro)',
      dateFound: '2025-09-18',
      locationLost: 'Conference Room',
      cashValue: '120000',
      comments: 'Space gray MacBook Pro with stickers on the cover. Found with charger.',
      finderName: 'Housekeeping Staff',
      firstName: 'Sarah',
      surname: 'Johnson',
      dateLost: '2025-09-18',
      countryCode: '+44',
      phoneNumber: '7700-900123',
      email: 'sarah.j@example.com',
      address: '45 Park Lane, London',
      country: 'United Kingdom',
      organisation: 'London Business Academy',
      foundByNonHotelStaff: '',
      imageUrl: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8aXBob25lfGVufDB8fDB8fHww'
    },
    
    // Variation 5: Clothing
    {
      itemType: 'Jacket',
      itemName: 'Designer Jacket',
      dateFound: '2025-08-30',
      locationLost: 'Coat Check',
      cashValue: '35000',
      comments: 'Black Gucci jacket, size M. Has a distinctive red and green stripe pattern.',
      finderName: 'Alex Kim',
      firstName: 'Li',
      surname: 'Wei',
      dateLost: '2025-08-29',
      countryCode: '+86',
      phoneNumber: '131-2345-6789',
      email: 'li.wei@example.com',
      address: '888 Nanjing Road, Shanghai',
      country: 'China',
      organisation: '',
      foundByNonHotelStaff: 'Event Staff',
      imageUrl: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8amFja2V0fGVufDB8fDB8fHww'
    }
  ];

  // Check if user is staff or admin
  useEffect(() => {
    async function checkUserRole() {
      if (currentUser) {
        const idTokenResult = await currentUser.getIdTokenResult();
        const isAdmin = idTokenResult.claims.admin === true;
        const isStaff = idTokenResult.claims.staff === true;
        setIsStaffOrAdmin(isAdmin || isStaff);
      }
    }
    checkUserRole();
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && isStaffOrAdmin) {
      // For staff/admin users, always set and maintain their name as finder
      const staffFinderName = currentUser.displayName || currentUser.email || '';
      setFinderName(staffFinderName);
    } else if (currentUser && !finderName && !isStaffOrAdmin) {
      // For non-staff users, only set if finderName is empty
      setFinderName(currentUser.displayName || '');
    }
  }, [currentUser, finderName, isStaffOrAdmin]);

  // Function to populate form with test data
  function populateTestData() {
    // Get the current variation and increment counter for next click
    const currentVariation = testDataVariations[testDataCounter.current];
    testDataCounter.current = (testDataCounter.current + 1) % testDataVariations.length;
    
    // Set all form fields with the current variation data
    setItemType(currentVariation.itemType);
    setItemName(currentVariation.itemName);
    setDateFound(currentVariation.dateFound);
    // For test data, randomly populate one of the location fields
    setRoomNumber('');
    setHotelComplex('');
    setOtherLocation('');
    const locationVariation = testDataCounter.current % 3;
    if (locationVariation === 0) {
      setRoomNumber(currentVariation.locationLost);
    } else if (locationVariation === 1) {
      setHotelComplex(currentVariation.locationLost);
    } else {
      setOtherLocation(currentVariation.locationLost);
    }
    setStorageLocation('Front Desk Safe'); // Default test storage location
    setDescription(currentVariation.comments);
    // Only set finderName for non-staff users
    if (!isStaffOrAdmin) {
      setFinderName(currentVariation.finderName);
    }
    setFirstName(currentVariation.firstName);
    setSurname(currentVariation.surname);
    setDateLost(currentVariation.dateLost);
    setCountryCode(currentVariation.countryCode);
    setPhoneNumber(currentVariation.phoneNumber);
    setEmail(currentVariation.email);
    setAddress(currentVariation.address);
    setCountry(currentVariation.country);
    setOrganisation(currentVariation.organisation);
    setFoundByNonHotelStaff(currentVariation.foundByNonHotelStaff);
    
    // Set the image preview
    if (currentVariation.imageUrl) {
      setImageUrls([currentVariation.imageUrl]);
      // Create a dummy File object for the image
      fetch(currentVariation.imageUrl)
        .then(response => response.blob())
        .then(blob => {
          const file = new File([blob], 'test-image.jpg', { type: 'image/jpeg' });
          setImages([file]);
        })
        .catch(error => console.error('Error loading test image:', error));
    }
  }

  const [images, setImages] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Append new files to existing ones instead of replacing
    const newImages = [...images, ...files];
    setImages(newImages);
    
    // Create preview URLs for new files and append to existing URLs
    const newUrls = files.map(file => URL.createObjectURL(file));
    const allUrls = [...imageUrls, ...newUrls];
    setImageUrls(allUrls);
    
    // Clear the file input so the same file can be selected again if needed
    e.target.value = '';
  };

  const handleRemoveImage = (index) => {
    const newImages = [...images];
    const newUrls = [...imageUrls];
    
    newImages.splice(index, 1);
    newUrls.splice(index, 1);
    
    setImages(newImages);
    setImageUrls(newUrls);
  };

  const uploadImages = async (itemId) => {
    const uploadedUrls = [];
    
    for (const image of images) {
      // Create a unique filename including hotelId
      const filename = `${Date.now()}-${image.name}`;
       // Include hotelId in the storage path
      const storageRef = ref(storage, `found-items/${hotelId}/${itemId}/${filename}`);
      
      try {
        // Upload the image
        await uploadBytes(storageRef, image);
        
        // Get the download URL
        const url = await getDownloadURL(storageRef);
        uploadedUrls.push(url);
      } catch (error) {
        console.error('Error uploading image:', error);
        throw error;
      }
    }
    
    return uploadedUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Form validation - itemName is no longer required
    if (!itemType || !dateFound) {
      setError(t('foundItemRegistration.errors.requiredFields'));
      return;
    }

    // Validate that at least one location field is filled
    if (!roomNumber.trim() && !hotelComplex.trim() && !otherLocation.trim()) {
      setError(t('foundItemRegistration.errors.locationRequired'));
      return;
    }

    // Validate that at least one photo is uploaded
    if (images.length === 0) {
      setError(t('foundItemRegistration.errors.photosRequired'));
      return;
    }

    await handleProhibitedItemsAcknowledged();
    return;
  };

  const handleProhibitedItemsAcknowledged = async () => {
    try {
      setLoading(true);

      // Add document to Firestore, including hotelId and user ID
      const itemData = {
        hotelId, // Add hotelId
        itemType,
        itemName,
        dateFound,
        roomNumber,
        hotelComplex,
        otherLocation,
        storageLocation,
        description,
        finderName,
        firstName,
        surname,
        dateLost,
        phone: countryCode && phoneNumber ? `${countryCode} ${phoneNumber}` : '',
        email,
        address,
        country,
        organisation,
        foundByNonHotelStaff,
        status: 'pending',
        createdAt: serverTimestamp(),
        matchId: null,
        foundByUserId: currentUser?.uid || null, // Store the UID of the user who registered it
        foundByUserEmail: currentUser?.email || null, // Store the email of the user who registered it
        foundByStaff: isStaffOrAdmin, // Indicate if registered by staff/admin
        viewed: false, // Initially set as unviewed
      };

      const docRef = await addDoc(collection(db, 'found-items'), itemData);
      console.log('Found item registered with ID:', docRef.id, 'viewed:', itemData.viewed);

      // Handle image upload
      if (images.length > 0) {
        setUploadingImages(true);
        const uploadedUrls = await uploadImages(docRef.id);
        setUploadingImages(false);
        
        // Update document with image URLs
        await updateDoc(docRef, {
          imageUrls: uploadedUrls
        });
      }

      // Reset form
      setItemType('');
      setItemName('');
      setDateFound(getTodayDate()); // Reset to today's date
      setRoomNumber('');
      setHotelComplex('');
      setOtherLocation('');
      setStorageLocation('');
      setDescription('');
      // Only reset finderName for non-staff users
      if (!isStaffOrAdmin) {
        setFinderName('');
      }
      setFirstName('');
      setSurname('');
      setDateLost('');
      setCountryCode('');
      setPhoneNumber('');
      setEmail('');
      setAddress('');
      setCountry('');
      setOrganisation('');
      setFoundByNonHotelStaff('');
      setImages([]);
      setImageUrls([]);
      setError('');

      // Check if user is staff-only (staff but not admin)
      const idTokenResult = await currentUser.getIdTokenResult();
      const isStaffOnly = idTokenResult.claims.staff === true && idTokenResult.claims.admin !== true;

      if (isStaffOnly) {
        // Staff-only users see success message
        setSubmitted(true);
        setSubmittedItemId(docRef.id);
      } else {
        // Admin users navigate back to admin dashboard
        navigate(`/${hotelId}/admin`);
      }
    } catch (error) {
      console.error('Error adding document: ', error);
      setError(t('foundItemRegistration.errors.submitFailed'));
    }

    setLoading(false);
  };

  // Country mapping for auto-sync
  const countryCodeToCountryMap = {
    '+1': 'United States', // Default to US for +1
    '+44': 'United Kingdom',
    '+33': 'France',
    '+49': 'Germany',
    '+39': 'Italy',
    '+34': 'Spain',
    '+31': 'Netherlands',
    '+32': 'Belgium',
    '+41': 'Switzerland',
    '+43': 'Austria',
    '+45': 'Denmark',
    '+46': 'Sweden',
    '+47': 'Norway',
    '+358': 'Finland',
    '+351': 'Portugal',
    '+30': 'Greece',
    '+48': 'Poland',
    '+420': 'Czech Republic',
    '+36': 'Hungary',
    '+81': 'Japan',
    '+82': 'South Korea',
    '+86': 'China',
    '+91': 'India',
    '+65': 'Singapore',
    '+852': 'Hong Kong',
    '+853': 'Macau',
    '+886': 'Taiwan',
    '+66': 'Thailand',
    '+60': 'Malaysia',
    '+62': 'Indonesia',
    '+63': 'Philippines',
    '+84': 'Vietnam',
    '+61': 'Australia',
    '+64': 'New Zealand',
    '+55': 'Brazil',
    '+52': 'Mexico',
    '+54': 'Argentina',
    '+56': 'Chile',
    '+57': 'Colombia',
    '+51': 'Peru',
    '+27': 'South Africa',
    '+20': 'Egypt',
    '+971': 'UAE',
    '+966': 'Saudi Arabia',
    '+972': 'Israel',
    '+90': 'Turkey',
    '+7': 'Russia',
  };

  // Auto-sync country when country code changes
  const handleCountryCodeChange = (newCountryCode) => {
    setCountryCode(newCountryCode);
    
    // Auto-set country if we have a mapping
    const mappedCountry = countryCodeToCountryMap[newCountryCode];
    if (mappedCountry) {
      setCountry(mappedCountry);
    }
  };

  if (submitted) {
    return (
      <div className="item-registration-container">
        <h2>{t('foundItemRegistration.successTitle')}</h2>
        <div className="success-message">
          <p>{t('foundItemRegistration.successMessage.line1')}</p>
          <p>{t('foundItemRegistration.successMessage.line2')}</p>
          <button           onClick={() => {
            setSubmitted(false);
            // Reset form for another entry
            setItemType('');
            setItemName('');
            setDateFound(getTodayDate()); // Reset to today's date
            setRoomNumber('');
            setHotelComplex('');
            setOtherLocation('');
            setStorageLocation('');
            setDescription('');
            // Only reset finderName for non-staff users
            if (!isStaffOrAdmin) {
              setFinderName('');
            }
            setFirstName('');
            setSurname('');
            setDateLost('');
            setCountryCode('');
            setPhoneNumber('');
            setEmail('');
            setAddress('');
            setCountry('');
            setOrganisation('');
            setFoundByNonHotelStaff('');
            setImages([]);
            setImageUrls([]);
          }}>
            {t('foundItemRegistration.buttons.registerAnother')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="item-registration-container">
      <h2>{t('foundItemRegistration.title')}</h2>
      <p className="required-fields-note">{t('foundItemRegistration.requiredFieldsNote')}</p>
      {error && <div className="error">{error}</div>}
      
      <div className="test-button-container">
        <button 
          type="button" 
          onClick={populateTestData} 
          className="test-button"
        >
          {t('foundItemRegistration.buttons.fillTestData', { count: testDataCounter.current + 1, total: 5 })}
        </button>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>{t('foundItemRegistration.sections.itemDetails')}</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label className="required">{t('foundItemRegistration.fields.itemType')}</label>
              <ItemTypeDropdown
                value={itemType}
                onChange={setItemType}
                placeholder={t('foundItemRegistration.placeholders.itemType')}
              />
            </div>
            
            <div className="form-group">
              <label>{t('foundItemRegistration.fields.itemName')}</label>
              <input
                type="text"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label className="required">{t('foundItemRegistration.fields.dateFound')}</label>
              <div className="date-input-container">
                <input
                  type="date"
                  value={dateFound}
                  onChange={(e) => setDateFound(e.target.value)}
                  required
                />
                {dateFound && (
                  <button
                    type="button"
                    className="clear-date-button"
                    onClick={() => setDateFound('')}
                    title="Clear date"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            
            <div className="form-group">
              <label>{t('foundItemRegistration.fields.finderName')}</label>
              <input
                type="text"
                value={finderName}
                onChange={(e) => setFinderName(e.target.value)}
                readOnly={isStaffOrAdmin}
                className={isStaffOrAdmin ? 'readonly-field' : ''}
                placeholder={isStaffOrAdmin ? t('foundItemRegistration.placeholders.autoFilled') : ''}
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>{t('foundItemRegistration.fields.roomNumber')}</label>
              <input
                type="text"
                value={roomNumber}
                onChange={(e) => setRoomNumber(e.target.value)}
                placeholder="e.g., 101, 1205A"
              />
            </div>
            
            <div className="form-group">
              <label>{t('foundItemRegistration.fields.hotelComplex')}</label>
              <input
                type="text"
                value={hotelComplex}
                onChange={(e) => setHotelComplex(e.target.value)}
                placeholder="e.g., Lobby, Restaurant, Pool area"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>{t('foundItemRegistration.fields.otherLocation')}</label>
              <input
                type="text"
                value={otherLocation}
                onChange={(e) => setOtherLocation(e.target.value)}
                placeholder="e.g., Car park, Driveway, Garden"
              />
            </div>
            
            <div className="form-group">
              <label>{t('foundItemRegistration.fields.storageLocation')}</label>
              <input
                type="text"
                value={storageLocation}
                onChange={(e) => setStorageLocation(e.target.value)}
                placeholder="e.g., Front Desk Safe, Lost & Found Room, Security Office"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>{t('foundItemRegistration.fields.description')}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label className="required">{t('foundItemRegistration.fields.photos')}</label>
            <div className="image-upload-container">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageChange}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
              />
              {imageUrls.length === 0 ? (
                <div className="upload-area">
                  <div className="upload-icon" style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>📷</div>
                  <button
                    type="button"
                    className="upload-button"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <span style={{ fontSize: '1.2em' }}>📷</span>
                    {t('foundItemRegistration.buttons.selectImages')}
                  </button>
                  <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                    {t('foundItemRegistration.help.dragDrop', 'Drag and drop images here or click to browse')}
                  </p>
                </div>
              ) : (
                <div>
                  <button
                    type="button"
                    className="upload-button"
                    onClick={() => fileInputRef.current.click()}
                    style={{ marginBottom: '1rem' }}
                  >
                    <span style={{ fontSize: '1.2em' }}>📷</span>
                    {t('foundItemRegistration.buttons.addMoreImages', 'Add More Images')}
                  </button>
                </div>
              )}
              
              <div className="image-preview-container">
                {imageUrls.map((url, index) => (
                  <div key={index} className="image-preview">
                    <img src={url} alt={`${t('foundItemRegistration.alt.preview')} ${index + 1}`} />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() => handleRemoveImage(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h3>{t('foundItemRegistration.sections.ownerInfo')}</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>{t('foundItemRegistration.fields.firstName')}</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            
            <div className="form-group">
              <label>{t('foundItemRegistration.fields.surname')}</label>
              <input
                type="text"
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>{t('foundItemRegistration.fields.dateLost')}</label>
              <div className="date-input-container">
                <input
                  type="date"
                  value={dateLost}
                  onChange={(e) => setDateLost(e.target.value)}
                />
                {dateLost && (
                  <button
                    type="button"
                    className="clear-date-button"
                    onClick={() => setDateLost('')}
                    title="Clear date"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>{t('foundItemRegistration.fields.phone')}</label>
              <div className="phone-input-container">
                <CountryCodeDropdown
                  value={countryCode}
                  onChange={handleCountryCodeChange}
                  placeholder={t('foundItemRegistration.placeholders.countryCode')}
                />
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder={t('foundItemRegistration.placeholders.phoneNumber')}
                  className="phone-number-input"
                />
              </div>
            </div>
          </div>
          
          <div className="form-group">
            <label>{t('foundItemRegistration.fields.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label>{t('foundItemRegistration.fields.address')}</label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>{t('foundItemRegistration.fields.country')}</label>
              <CountryDropdown
                value={country}
                onChange={setCountry}
                placeholder={t('foundItemRegistration.placeholders.country')}
              />
            </div>
            
            <div className="form-group">
              <label>{t('foundItemRegistration.fields.organisation')}</label>
              <input
                type="text"
                value={organisation}
                onChange={(e) => setOrganisation(e.target.value)}
                placeholder="e.g., Tokyo School, Manchester United FC, ABC Corporation"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>{t('foundItemRegistration.fields.foundByNonStaff')}</label>
              <input
                type="text"
                value={foundByNonHotelStaff}
                onChange={(e) => setFoundByNonHotelStaff(e.target.value)}
                placeholder={t('foundItemRegistration.placeholders.foundByNonStaff')}
              />
            </div>
          </div>
        </div>
        
        <div className="form-actions">
          <button
            type="submit"
            disabled={loading || uploadingImages}
          >
            {uploadingImages ? t('foundItemRegistration.buttons.uploadingImages') : t('foundItemRegistration.buttons.registerItem')}
          </button>
        </div>
      </form>

    </div>
  );
} 
